import type { ApiEndpoint } from '../config/apis';

type QueryValue = string | number | boolean | null | undefined;

export type ApiFieldError = {
  field: string;
  reason: string;
};

export type ApiRequestOptions<TBody = unknown> = {
  body?: TBody;
  headers?: HeadersInit;
  query?: Record<string, QueryValue>;
  signal?: AbortSignal;
  useAuth?: boolean;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  fieldErrors: ApiFieldError[];

  constructor(message: string, status: number, code?: string, fieldErrors: ApiFieldError[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
export const ACCESS_TOKEN_STORAGE_KEY = 'accessToken';
const LOGIN_TRX_ID_STORAGE_KEY = 'loginTrxId';
const LATEST_LOGIN_TRX_ID_STORAGE_KEY = 'latestLoginTrxId';
const USER_DISPLAY_NAME_STORAGE_KEY = 'userDisplayName';
const USER_ROLE_STORAGE_KEY = 'userRole';
const SELECTED_EVENT_ID_STORAGE_KEY = 'selectedEventId';
const SELECTED_EVENT_NAME_STORAGE_KEY = 'selectedEventName';
const SERVER_STATUS_MESSAGE_STORAGE_KEY = 'serverStatusMessage';
const SESSION_EXPIRED_MESSAGE_STORAGE_KEY = 'sessionExpiredMessage';
const LOGIN_PATH = '/login';
const SESSION_EXPIRED_MESSAGE = '다시 로그인해 주세요.';
export const NETWORK_ERROR_MESSAGE =
  '네트워크 연결이 불안정합니다. 인터넷 연결을 확인한 뒤 다시 시도해 주세요.';
const SERVER_UNAVAILABLE_MESSAGE = '현재 서버가 불안정합니다. 잠시 후 다시 시도해 주세요.';

export const AUTHENTICATION_STATE_CLEARED_EVENT = 'authenticationStateCleared';
export const SESSION_EXPIRED_EVENT = 'sessionExpired';

export function getAccessToken() {
  const storedToken = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);

  if (storedToken) {
    return storedToken;
  }

  const legacySessionToken = sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);

  if (legacySessionToken) {
    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, legacySessionToken);
  }

  return legacySessionToken;
}

function buildUrl(path: string, query?: Record<string, QueryValue>) {
  const baseUrl = API_BASE_URL || globalThis.location.origin;
  const url = new URL(path, baseUrl);

  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.set(key, String(value));
    }
  });

  if (API_BASE_URL) {
    return url.toString();
  }

  return `${url.pathname}${url.search}`;
}

async function readResponseBody(response: Response) {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function getErrorMessage(body: unknown, fallback: string) {
  if (typeof body === 'object' && body !== null) {
    const maybeBody = body as { message?: unknown; code?: unknown; error?: unknown };

    if (typeof maybeBody.message === 'string') {
      return maybeBody.message;
    }

    if (typeof maybeBody.error === 'string') {
      return maybeBody.error;
    }
  }

  return fallback;
}

function getFallbackErrorMessage(status: number) {
  if (status === 401) {
    return '로그인이 필요합니다. 다시 로그인해 주세요.';
  }

  if (status === 403) {
    return '이 작업을 수행할 권한이 없습니다.';
  }

  if (status === 404) {
    return '요청한 정보를 찾을 수 없습니다.';
  }

  return '요청 처리 중 오류가 발생했습니다.';
}

function getErrorCode(body: unknown) {
  if (typeof body === 'object' && body !== null) {
    const maybeBody = body as { code?: unknown };

    if (typeof maybeBody.code === 'string' || typeof maybeBody.code === 'number') {
      return String(maybeBody.code);
    }
  }

  return undefined;
}

function getFieldErrors(body: unknown) {
  if (typeof body !== 'object' || body === null) {
    return [];
  }

  const maybeBody = body as { errors?: unknown };

  if (!Array.isArray(maybeBody.errors)) {
    return [];
  }

  return maybeBody.errors.flatMap((error) => {
    if (typeof error !== 'object' || error === null) {
      return [];
    }

    const maybeError = error as { field?: unknown; reason?: unknown };

    if (typeof maybeError.field !== 'string' || typeof maybeError.reason !== 'string') {
      return [];
    }

    return [{ field: maybeError.field, reason: maybeError.reason }];
  });
}

function isUnauthorizedError(status: number, code?: string) {
  return status === 401 || code?.startsWith('401') === true;
}

function isAuthenticationErrorCode(code?: string) {
  return code?.startsWith('401') === true;
}

function decodeBase64Url(value: string) {
  const base64 = value.replaceAll('-', '+').replaceAll('_', '/');
  const paddingLength = (4 - (base64.length % 4)) % 4;
  return globalThis.atob(`${base64}${'='.repeat(paddingLength)}`);
}

function hasExpiredAccessToken(token: string) {
  const [, payload] = token.split('.');

  if (!payload) {
    return false;
  }

  try {
    const parsedPayload = JSON.parse(decodeBase64Url(payload)) as { exp?: unknown };

    return typeof parsedPayload.exp === 'number' && parsedPayload.exp * 1000 <= Date.now();
  } catch {
    return false;
  }
}

function clearAuthenticationState() {
  localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
  sessionStorage.removeItem(LOGIN_TRX_ID_STORAGE_KEY);
  localStorage.removeItem(LATEST_LOGIN_TRX_ID_STORAGE_KEY);
  sessionStorage.removeItem(USER_DISPLAY_NAME_STORAGE_KEY);
  sessionStorage.removeItem(USER_ROLE_STORAGE_KEY);
  sessionStorage.removeItem(SELECTED_EVENT_ID_STORAGE_KEY);
  sessionStorage.removeItem(SELECTED_EVENT_NAME_STORAGE_KEY);

  if (typeof globalThis.dispatchEvent === 'function' && typeof Event === 'function') {
    globalThis.dispatchEvent(new Event(AUTHENTICATION_STATE_CLEARED_EVENT));
  }
}

function redirectToLogin() {
  if (globalThis.location.pathname === LOGIN_PATH) {
    return;
  }

  globalThis.location.replace(`${LOGIN_PATH}?reason=session_expired`);
}

export function handleUnauthorizedAccess() {
  sessionStorage.setItem(SESSION_EXPIRED_MESSAGE_STORAGE_KEY, SESSION_EXPIRED_MESSAGE);
  clearAuthenticationState();

  if (typeof globalThis.dispatchEvent === 'function' && typeof Event === 'function') {
    globalThis.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    return;
  }

  redirectToLogin();
}

export function consumeServerStatusMessage() {
  const message = sessionStorage.getItem(SERVER_STATUS_MESSAGE_STORAGE_KEY);
  sessionStorage.removeItem(SERVER_STATUS_MESSAGE_STORAGE_KEY);
  return message;
}

export function consumeSessionExpiredMessage() {
  const message = sessionStorage.getItem(SESSION_EXPIRED_MESSAGE_STORAGE_KEY);
  sessionStorage.removeItem(SESSION_EXPIRED_MESSAGE_STORAGE_KEY);
  return message;
}

function isServerUnavailableStatus(status: number) {
  return status === 502 || status === 503 || status === 504;
}

function handleServerUnavailableAccess() {
  sessionStorage.setItem(SERVER_STATUS_MESSAGE_STORAGE_KEY, SERVER_UNAVAILABLE_MESSAGE);
}

function buildRequestBody(body: unknown): BodyInit | undefined {
  if (body === undefined) {
    return undefined;
  }

  if (body instanceof FormData) {
    return body;
  }

  return JSON.stringify(body);
}

function createPendingResponse<TResponse>() {
  return new Promise<TResponse>(() => {});
}

function shouldAttachAuthorization(headers: Headers, token: string | null, useAuth?: boolean) {
  return useAuth !== false && Boolean(token) && !headers.has('Authorization');
}

function attachAuthorizationOrHandleExpiry<TResponse>(
  headers: Headers,
  token: string | null,
  useAuth?: boolean
) {
  if (!shouldAttachAuthorization(headers, token, useAuth)) {
    return null;
  }

  if (hasExpiredAccessToken(token ?? '')) {
    handleUnauthorizedAccess();
    return createPendingResponse<TResponse>();
  }

  headers.set('Authorization', `Bearer ${token}`);
  return null;
}

function handleAuthenticationResponse<TResponse>(
  response: Response,
  errorCode: string | undefined,
  useAuth?: boolean
) {
  if (useAuth === false) {
    return null;
  }

  if (response.ok && isAuthenticationErrorCode(errorCode)) {
    handleUnauthorizedAccess();
    return createPendingResponse<TResponse>();
  }

  if (!response.ok && isUnauthorizedError(response.status, errorCode)) {
    handleUnauthorizedAccess();
    return createPendingResponse<TResponse>();
  }

  return null;
}

export async function apiRequest<TResponse, TBody = unknown>(
  endpoint: ApiEndpoint,
  options: ApiRequestOptions<TBody> = {}
) {
  const headers = new Headers(options.headers);
  const token = getAccessToken();
  const hasBody = options.body !== undefined;
  const isFormData = options.body instanceof FormData;
  const requestBody = buildRequestBody(options.body);

  if (hasBody && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json;charset=utf-8');
  }

  const pendingAuthResponse = attachAuthorizationOrHandleExpiry<TResponse>(
    headers,
    token,
    options.useAuth
  );

  if (pendingAuthResponse) {
    return pendingAuthResponse;
  }

  let response: Response;

  try {
    response = await fetch(buildUrl(endpoint.path, options.query), {
      method: endpoint.method,
      headers,
      body: requestBody,
      signal: options.signal,
    });
  } catch {
    if (options.useAuth !== false) {
      sessionStorage.setItem(SERVER_STATUS_MESSAGE_STORAGE_KEY, NETWORK_ERROR_MESSAGE);
    }
    throw new ApiError(NETWORK_ERROR_MESSAGE, 0);
  }

  const body = await readResponseBody(response);
  const errorCode = getErrorCode(body);
  const fieldErrors = getFieldErrors(body);

  const pendingUnauthorizedResponse = handleAuthenticationResponse<TResponse>(
    response,
    errorCode,
    options.useAuth
  );

  if (pendingUnauthorizedResponse) {
    return pendingUnauthorizedResponse;
  }

  if (!response.ok) {
    if (isServerUnavailableStatus(response.status) && options.useAuth !== false) {
      handleServerUnavailableAccess();
    }

    throw new ApiError(
      getErrorMessage(body, getFallbackErrorMessage(response.status)),
      response.status,
      errorCode,
      fieldErrors
    );
  }

  return body as TResponse;
}

// 파일 응답(PDF/XLSX/CSV)을 Blob으로 받는다. 실패 시 JSON 에러 본문을 ApiError로 변환한다.
export async function apiDownload(
  endpoint: ApiEndpoint,
  options: Pick<ApiRequestOptions, 'query' | 'signal'> = {}
) {
  const headers = new Headers();
  const token = getAccessToken();

  if (token) {
    if (hasExpiredAccessToken(token)) {
      handleUnauthorizedAccess();
      return createPendingResponse<Blob>();
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(endpoint.path, options.query), {
      method: endpoint.method,
      headers,
      signal: options.signal,
    });
  } catch {
    throw new ApiError(NETWORK_ERROR_MESSAGE, 0);
  }

  if (response.ok) {
    return response.blob();
  }

  const body = await readResponseBody(response);
  const errorCode = getErrorCode(body);

  if (isUnauthorizedError(response.status, errorCode)) {
    handleUnauthorizedAccess();
    return createPendingResponse<Blob>();
  }

  throw new ApiError(
    getErrorMessage(body, getFallbackErrorMessage(response.status)),
    response.status,
    errorCode,
    getFieldErrors(body)
  );
}

export function saveAccessToken(accessToken: string) {
  localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
  sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
}

export function clearAccessToken() {
  clearAuthenticationState();
}
